import {
    Component,
    ChangeDetectionStrategy,
    ElementRef,
    ViewChild,
    Output,
    EventEmitter,
    Input,
    ChangeDetectorRef,
    HostBinding,
} from '@angular/core';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { Router } from '@angular/router';
import { SearchResult, SearchService } from './search.service';
import { GoogleAnalyticsService } from '../../../google-analytics.service';
import { BehaviorSubject } from 'rxjs';
import { StationNavigationService } from 'shared/stations-service/station-navigation.service';
import { EnvironmentService } from 'providers/environment.service';
import { DialogLinkService } from 'shared/routed-dialog/dialog-link.service';

@Component({
    selector: 'ce-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent {
    @Input() @HostBinding('class') public themeClass: 'theme-mykempower' | 'theme-chargeye' = 'theme-chargeye';

    @ViewChild('inputField') inputField: ElementRef;
    @Output() searchFocused = new EventEmitter<boolean>();
    text: string;

    isSearchActive$ = new BehaviorSubject(false);

    private pendingActivation = false;
    private blurTimeout: ReturnType<typeof setTimeout>;
    private selectionInProgress = false;

    constructor(
        public search: SearchService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private navigation: StationNavigationService,
        private cdRef: ChangeDetectorRef,
        private environmentService: EnvironmentService,
        private router: Router,
        private dialogLinkService: DialogLinkService
    ) {
        this.environmentService.getAppOrigin$().subscribe((origin) => {
            this.themeClass = `theme-${origin}`;
            this.cdRef.markForCheck();
        });
    }

    ngAfterViewInit(): void {
        // Get the mat-form-field element for transition listening
        const formField = this.inputField.nativeElement.closest('mat-form-field');

        // Listen for transition end to show history after animation completes
        const onTransitionEnd = () => {
            if (this.pendingActivation) {
                this.pendingActivation = false;
                this.search.setActive(true);
            }
        };
        formField?.addEventListener('transitionend', onTransitionEnd);

        // Subscribe to input focus event
        this.inputField.nativeElement.addEventListener('focus', () => {
            // Cancel any pending blur cleanup
            if (this.blurTimeout) {
                clearTimeout(this.blurTimeout);
            }
            this.selectionInProgress = false;
            this.pendingActivation = true;

            this.isSearchActive$.next(true);
        });
        this.inputField.nativeElement.addEventListener('blur', () => {
            // Cancel pending activation if user blurs before animation completes
            this.pendingActivation = false;
            // Always start the cleanup timeout - handleSelection will cancel it if needed
            this.blurTimeout = setTimeout(() => {
                this.selectionInProgress = false;
                this.isSearchActive$.next(false);
                this.search.setActive(false);
                this.inputField.nativeElement.value = '';
                this.search.clear();
            }, 200);
        });
        this.isSearchActive$.subscribe((isActive) => {
            this.searchFocused.emit(isActive);
        });
    }

    displayFn(_result: SearchResult): string {
        return ''; // Return empty string to prevent [object Object] display
    }

    onOptionClick(res: SearchResult): void {
        // Always handle selection on click - this catches cases where optionSelected doesn't fire
        if (res) {
            this.handleSelection(res);
        }
    }

    onOptionMousedown(_event: MouseEvent, _res: SearchResult): void {
        this.selectionInProgress = true;
        // Cancel the blur timeout since we're selecting an option
        if (this.blurTimeout) {
            clearTimeout(this.blurTimeout);
        }
    }

    onRemoveFromHistory(event: MouseEvent, result: SearchResult): void {
        event.stopPropagation();
        event.preventDefault();
        this.search.removeFromHistory(result);
    }

    onOptionSelected(event: MatAutocompleteSelectedEvent): void {
        const searchResult = event.option.value as SearchResult;
        if (searchResult) {
            this.handleSelection(searchResult);
        }
    }

    private handleSelection(searchResult: SearchResult): void {
        // Prevent double handling if both click and optionSelected fire
        if (!this.isSearchActive$.getValue()) {
            return;
        }

        // Cancel the blur timeout since we're handling the selection
        if (this.blurTimeout) {
            clearTimeout(this.blurTimeout);
        }

        // Clear selection flag and cleanup
        this.selectionInProgress = false;
        this.isSearchActive$.next(false);
        this.search.setActive(false);
        this.text = '';
        this.inputField.nativeElement.value = '';
        this.inputField.nativeElement.blur(); // Blur the input to collapse it
        this.search.clear();

        if (searchResult.type !== 'no_results') {
            this.search.addToHistory(searchResult);
            this.googleAnalyticsService.sendGlobalSearchToGA();
            this.navigateToResult(searchResult);
        }
    }

    private navigateToResult(result: SearchResult): void {
        switch (result.type) {
            case 'station': {
                const stationId = result.globalSearchStation?.stationId ?? result.station?.data?.station?.stationId;
                if (stationId) {
                    this.router.navigate(['/locations', 'station', stationId]);
                }
                break;
            }
            case 'location': {
                const locationId = result.globalSearchLocation?.locationUid ?? result.location?.id;
                if (locationId) {
                    this.router.navigate(['/locations', 'l', 'location', locationId]);
                }
                break;
            }
            case 'organization': {
                const tenantId = result.globalSearchOrganization?.tenantId;
                if (tenantId) {
                    this.router.navigate(['/admin', 'iam', 'organizations', 'id', tenantId]);
                }
                break;
            }
            case 'connector': {
                const stationId =
                    result.globalSearchConnector?.stationId ??
                    result.globalSearchConnector?.station?.stationId ??
                    result.connector?.stationId;
                const evseId = result.globalSearchConnector?.evseId ?? result.connector?.evseId;
                if (stationId && evseId) {
                    this.dialogLinkService.open(['connector', stationId, evseId]);
                }
                break;
            }
            case 'ticket': {
                const ticketId = result.ticket?.ticketId;
                if (ticketId) {
                    this.router.navigate(['/tickets', ticketId]);
                }
                break;
            }
        }
    }

    onPaste($event: ClipboardEvent) {
        const pastedText = $event.clipboardData.getData('text');
        this.navigation.tryNavigateToStation(pastedText, (navigated: boolean) => {
            if (navigated) {
                // Clear the search field if a station was found
                this.search.clear();
            }
        });
    }
}
